<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Comparativo de Equipes',
  },
});

const sortedTeams = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return [...props.data].sort(
    (a, b) => b.total_conversations - a.total_conversations
  );
});

const maxConversations = computed(() => {
  if (sortedTeams.value.length === 0) return 1;
  return Math.max(...sortedTeams.value.map(t => t.total_conversations), 1);
});

const teamColors = [
  'bg-n-blue-9',
  'bg-n-teal-9',
  'bg-n-violet-9',
  'bg-n-amber-9',
  'bg-n-pink-9',
  'bg-n-cyan-9',
];

function getTeamColor(index) {
  return teamColors[index % teamColors.length];
}

function formatRate(rate) {
  if (!rate && rate !== 0) return '-';
  return `${rate}%`;
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-users text-lg text-n-violet-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="sortedTeams.length === 0"
      class="flex flex-col items-center justify-center h-48 text-n-slate-11"
    >
      <span class="i-lucide-users text-4xl mb-2 opacity-50" />
      <span class="text-sm">Sem equipes configuradas</span>
    </div>

    <!-- Teams List -->
    <div v-else class="flex flex-col gap-4">
      <div
        v-for="(team, index) in sortedTeams"
        :key="team.team_id"
        class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-1"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="w-3 h-3 rounded" :class="getTeamColor(index)" />
            <span class="text-sm font-medium text-n-slate-12">{{
              team.team_name
            }}</span>
            <span class="text-xs text-n-slate-10">({{ team.agent_count }} agentes)</span>
          </div>
          <span class="text-lg font-bold text-n-slate-12">
            {{ team.total_conversations }}
          </span>
        </div>

        <!-- Progress bar -->
        <div class="h-2 bg-n-alpha-2 rounded-full overflow-hidden">
          <div
            class="h-full rounded-full transition-all duration-300"
            :class="getTeamColor(index)"
            :style="{
              width: `${(team.total_conversations / maxConversations) * 100}%`,
            }"
          />
        </div>

        <!-- Team metrics -->
        <div class="grid grid-cols-4 gap-2 text-xs">
          <div class="text-center">
            <div class="font-medium text-n-teal-11">
              {{ team.total_resolved }}
            </div>
            <div class="text-n-slate-10">Resolvidas</div>
          </div>
          <div class="text-center">
            <div
              class="font-medium"
              :class="
                team.resolution_rate >= 70
                  ? 'text-n-teal-11'
                  : 'text-n-amber-11'
              "
            >
              {{ formatRate(team.resolution_rate) }}
            </div>
            <div class="text-n-slate-10">Taxa</div>
          </div>
          <div class="text-center">
            <div
              class="font-medium"
              :class="
                team.avg_csat >= 80
                  ? 'text-n-teal-11'
                  : team.avg_csat >= 60
                    ? 'text-n-amber-11'
                    : 'text-n-ruby-11'
              "
            >
              {{ formatRate(team.avg_csat) }}
            </div>
            <div class="text-n-slate-10">CSAT</div>
          </div>
          <div class="text-center">
            <div class="font-medium text-n-slate-12">
              {{ team.avg_per_agent }}
            </div>
            <div class="text-n-slate-10">Por agente</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
